import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Scoreboard } from 'components/Scoreboard/Scoreboard';
import * as S from './Game.style';
import * as SF from '../../components/Forms/Forms.style';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AddPlayerForm } from 'components/Forms/AddPlayerForm/AddPlayerForm';
import { ScoreUpdateForm } from 'components/Forms/ScoreUpdateForm/ScoreUpdateForm';
import { Rankings } from 'components/Rankings/Rankings';
import { DARTS_TARGET, GameType, useGame } from 'context/GameContext';
import { usePlayers } from 'context/PlayerContext';
import { Menu } from 'components/Menu/Menu';
import { Dialog } from 'components/Dialog/Dialog';
import { NumberInput } from 'components/Forms/NumberInput/NumberInput';

import { createDefaultScores } from 'utils/game';
import { Button, ButtonVariant } from 'components/Button/Button';
import { GameSettings } from 'components/Forms/GameSettingsForm/GameSettings';

import loop from 'assets/loop.svg';
import settings from 'assets/settings.svg';
import undo from 'assets/undo.svg';
import finalRoundImage from 'assets/finalRound.jpg';
import readySetGo from 'assets/audio/readySetGo.mp3';
import finalRound from 'assets/audio/finalRound.mp3';
import fanfareSuccess from 'assets/audio/fanfare-success.mp3';
import { RadioInput } from '../../components/Forms/RadioInput/RadioInput';
import { Popover } from '../../components/Popover/Popover';

export interface IPlayer {
  player: string;
  scores: Array<number | undefined>;
  totalPoints: number;
  isActive: boolean;
}

export type IFormFields = {
  name: string;
  players: IPlayer[];
  rounds: number;
  target?: number | undefined;
  type: GameType;
};

export const BASE_CONFIG = {
  points: [0, 1, 2, 3, 4, 5, 6, 7, 8, 10, 13],
};

export const DART_TARGET_OPTIONS = [
  {
    label: 'Cíl [31]',
    value: `${DARTS_TARGET.LOW}`,
  },
  {
    label: 'Cíl [51]',
    value: `${DARTS_TARGET.MEDIUM}`,
  },
  {
    label: 'Cíl [61]',
    value: `${DARTS_TARGET.HIGH}`,
  },
];

export const Game = () => {
  const [specificPoints, setSpecificPoints] = useState(0);
  const [isOpenPointsDialog, setIsOpenPointsDialog] = useState(false);
  const [isOpenFinalRoundDialog, setIsOpenFinalRoundDialog] = useState(false);
  const [isGameSettingsDialogOpen, setIsGameSettingsDialogOpen] =
    useState(false);

  const [targetHeight, setTargetHeight] = useState(0);
  const [roundsHeight, setRoundsHeight] = useState(0);

  const targetRef = useRef<HTMLDivElement>(null);
  const roundsRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<HTMLAudioElement | null>(null);
  const finalRef = useRef<HTMLAudioElement | null>(null);
  const fanfareSuccessRef = useRef<HTMLAudioElement | null>(null);

  const {
    isGameStarted,
    isGameFinished,
    gameSettings,
    startGame,
    resetGame,
    updateSettings,
    isLastRound,
    currentTurn,
  } = useGame();

  const {
    players,
    updatePlayers,
    updateClassicScore,
    undoScore,
    backToGame,
    popoverMessage,
  } = usePlayers();

  const defaultValues = {
    rounds: gameSettings.totalRounds,
    players: [
      {
        player: 'Player 1',
        scores: createDefaultScores(gameSettings.totalRounds),
        totalPoints: 0,
        isActive: true,
      },
    ],
  };

  const {
    handleSubmit,
    register,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IFormFields>({
    defaultValues,
  });

  const [selectedType, selectedTarget] = watch(['type', 'target']);

  const onSubmit: SubmitHandler<IFormFields> = (data) => {
    const isDarts: boolean = data.type === GameType.DARTS;
    const targetValue: number = isDarts
      ? Number(selectedTarget ?? DARTS_TARGET.LOW)
      : 0;

    const updatedPlayers = data.players.map((p) => ({
      ...p,
      totalPoints: isDarts ? targetValue : 0,
    }));

    const dartsSettings = isDarts
      ? {
          totalRounds: undefined,
          doublePointsStatus: false,
          target: targetValue,
        }
      : undefined;

    updateSettings({
      totalRounds: data.rounds,
      type: data.type,
      ...dartsSettings,
    });

    updatePlayers({
      ...data,
      players: updatedPlayers,
    });

    startGame();

    if (gameSettings.audioStatus) {
      startRef.current?.play();
    }

    sessionStorage.setItem('gameData', JSON.stringify(updatedPlayers));
  };

  const handleRoundsOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = Number(e.target.value);
    if (newValue < 0) {
      newValue = 0;
    }

    updateSettings({
      totalRounds: newValue,
    });
    setValue('rounds', newValue);
  };

  const handleAddPlayerDialog = () => {
    if (!isGameFinished) {
      setIsGameSettingsDialogOpen(true);
    }
  };

  const menuItems = [
    { label: 'Reset Hry', image: loop, handler: resetGame },
    {
      label: 'Nastavení',
      image: settings,
      handler: handleAddPlayerDialog,
    },
    { label: 'Zpět', image: undo, handler: undoScore },
  ];

  useLayoutEffect(() => {
    if (targetRef.current) {
      setTargetHeight(targetRef.current.scrollHeight);
    }
    if (roundsRef.current) {
      setRoundsHeight(roundsRef.current.scrollHeight);
    }
  }, [selectedType]);

  useEffect(() => {
    if (
      gameSettings.type === GameType.SUM_OF_POINTS &&
      isLastRound &&
      currentTurn === 0 &&
      gameSettings.totalRounds > 1
    ) {
      setIsOpenFinalRoundDialog(true);

      if (gameSettings.audioStatus) {
        setTimeout(() => {
          finalRef.current?.play();
        }, 300);
      }
    }
  }, [isLastRound]);

  useEffect(() => {
    if (
      gameSettings.type === GameType.DARTS &&
      isGameFinished
    ) {
      if (gameSettings.audioStatus) {
        setTimeout(() => {
          fanfareSuccessRef.current?.play();
        }, 300);
      }
    }
  }, [isGameFinished]);

  return (
    <>
      <audio ref={startRef} src={readySetGo} />
      <audio ref={finalRef} src={finalRound} />
      <audio ref={fanfareSuccessRef} src={fanfareSuccess} />
      <S.Container>
        <S.Header isGameActive={!isGameStarted}>Broos Game</S.Header>

        {!isGameStarted ? (
          <>
            <form onSubmit={handleSubmit(onSubmit)}>
              <S.FormItem>
                <S.FormTitle>Select Your Game-Type / Typ hry</S.FormTitle>
                <SF.RadioWrapper>
                  <RadioInput
                    label="Points / Na body"
                    name="type"
                    value={GameType.SUM_OF_POINTS}
                    register={register}
                    rules={{ required: true }}
                  />
                  <RadioInput
                    label="Darts / Šipky"
                    name="type"
                    value={GameType.DARTS}
                    register={register}
                    rules={{ required: true }}
                  />
                </SF.RadioWrapper>
                {errors.type && 'Please select a game type'}
              </S.FormItem>
              <S.Expandable
                expanded={selectedType === GameType.DARTS}
                height={targetHeight}
              >
                <div ref={targetRef}>
                  <SF.RadioWrapper>
                    {DART_TARGET_OPTIONS.map((option) => (
                      <RadioInput
                        key={option.value}
                        label={option.label}
                        name="target"
                        value={option.value}
                        register={register}
                        rules={{ required: selectedType === GameType.DARTS }}
                        isActive={selectedTarget === Number(option.value)}
                      />
                    ))}
                  </SF.RadioWrapper>
                  {errors.target && <div>Please select a target</div>}
                </div>
              </S.Expandable>
              <S.Expandable
                expanded={selectedType === GameType.SUM_OF_POINTS}
                height={roundsHeight}
              >
                <div ref={roundsRef}>
                  <NumberInput
                    label="Number of rounds / Počet kol"
                    name="rounds"
                    register={register}
                    rules={{ required: true, min: 1, max: 50 }}
                    errorMessage={
                      errors.rounds &&
                      'Please enter a valid number of rounds between 1 and 20'
                    }
                    defaultValue={gameSettings.totalRounds}
                    onFocus={(e) => {
                      e.target.value = '';
                    }}
                    onBlur={(e) => {
                      if (!e.target.value) {
                        setValue('rounds', gameSettings.totalRounds);
                      }
                    }}
                    onChange={handleRoundsOnChange}
                  />
                </div>
              </S.Expandable>
              <AddPlayerForm
                register={register}
                control={control}
                errors={errors}
                rounds={gameSettings.totalRounds}
              />
              <S.FormItem>
                <Button type="submit" variant={ButtonVariant.SUBMIT}>
                  Start
                </Button>
              </S.FormItem>
            </form>
            <S.ShopNote>
              Explore all products on{' '}
              <S.ShopLink
                href="https://www.broos-game.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Broos Store
              </S.ShopLink>
            </S.ShopNote>
          </>
        ) : (
          <>
            <ScoreUpdateForm
              openPointsDialog={() => setIsOpenPointsDialog(true)}
            />

            {!isGameFinished && (
              <>
                <Scoreboard />
                <Menu items={menuItems} />
                <Popover message={popoverMessage} />
              </>
            )}
          </>
        )}

        {isGameFinished && (
          <div style={{ textAlign: 'center' }}>
            <h2>Game Over</h2>
            <p style={{ fontWeight: 'bold' }}>
              That was fun! Here are the final standings:
            </p>
            <Rankings
              rankings={players}
              isDarts={gameSettings.type === GameType.DARTS}
            />
            <S.ActionsGameContainer>
              <Button variant={ButtonVariant.INFO} onClick={backToGame}>
                Back to Game
              </Button>
              <Button
                type="submit"
                variant={ButtonVariant.SUBMIT}
                onClick={resetGame}
              >
                Start New Game
              </Button>
            </S.ActionsGameContainer>
          </div>
        )}
      </S.Container>

      <Dialog
        isOpen={isOpenPointsDialog}
        onClose={() => setIsOpenPointsDialog(false)}
        submitButton={
          <Button
            type="submit"
            variant={ButtonVariant.SUBMIT}
            onClick={() => {
              updateClassicScore(specificPoints);
              setIsOpenPointsDialog(false);
            }}
          >
            Submit
          </Button>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div style={{ marginBottom: 24, fontSize: 24, fontWeight: 700 }}>
            Add Custom Points / Jiný počet
          </div>

          <NumberInput
            label="Custom points / Jiný počet"
            defaultValue={specificPoints}
            onChange={(e) => setSpecificPoints(Number(e.target.value))}
            name="customPoints"
          />
        </form>
      </Dialog>

      <Dialog
        style={{
          backgroundImage: finalRoundImage,
          padding: 0,
          margin: 0,
          backgroundColor: '#de4b1e',
        }}
        bgWrapper="#de4b1e"
        isOpen={isOpenFinalRoundDialog}
        onClose={() => setIsOpenFinalRoundDialog(false)}
        submitButton={
          <Button
            onClick={() => setIsOpenFinalRoundDialog(false)}
            variant={ButtonVariant.ADD}
          >
            Got it!
          </Button>
        }
      >
        <img
          src={finalRoundImage}
          style={{ width: '100%', padding: 0, margin: 0 }}
          alt="final round"
        />
      </Dialog>

      <GameSettings
        isOpen={isGameSettingsDialogOpen}
        onClose={() => setIsGameSettingsDialogOpen(false)}
      />
    </>
  );
};
